import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import type { ArticleData } from "@/mocks/articles";

interface ArticleLayoutProps {
  article: ArticleData;
  children: React.ReactNode;
}

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticleLayout({ article, children }: ArticleLayoutProps) {
  const canonicalUrl = `${siteUrl}/guide/${article.slug}`;
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "url": canonicalUrl,
    "image": article.heroImage,
    "datePublished": "2026-07-16",
    "dateModified": "2026-07-16",
    "author": {
      "@type": "Organization",
      "name": "OOPBUY Spreadsheet Deutschland"
    },
    "publisher": {
      "@type": "Organization",
      "name": "OOPBUY Spreadsheet Deutschland",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article>
        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />

        {/* Hero Header */}
        <header className="relative bg-secondary-900 pt-20 pb-12 md:pt-24 md:pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.heroImage}
              alt={article.title}
              className="w-full h-full object-cover object-top opacity-40"
            />
            <div className="absolute inset-0 bg-secondary-900/70"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs font-semibold text-accent-400 uppercase tracking-[0.2em] mb-4">
                {article.category}
              </p>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-3">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-background-300 max-w-2xl mx-auto mb-3">
                {article.subtitle}
              </p>
              <p className="text-xs text-background-400">
                Letztes Update: 16. Juli 2026 &middot; Lesezeit: {article.readTime}
              </p>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-3 bg-background-100 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
                Home
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-700 whitespace-nowrap">{article.category}</span>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-500 truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 space-y-5 leading-relaxed">
              {children}
            </div>

            {/* Internal Link Box */}
            <div className="mt-10 p-5 md:p-6 bg-primary-50 rounded-xl border border-primary-200/50">
              <h3 className="font-heading text-base md:text-lg font-semibold text-primary-800 mb-3">
                Weiterlesen
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-home-line"></i>
                    </span>
                    Zurück zur Startseite — Alle Kategorien und Produkte
                  </Link>
                </li>
                <li>
                  <Link
                    to="/sitemap"
                    className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    Alle Guides & Artikel — Sitemap
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-external-link-line"></i>
                    </span>
                    Direkt zu OOPBUY — Jetzt einkaufen
                  </a>
                </li>
              </ul>
            </div>

            {/* Related Categories */}
            <div className="mt-8">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                Beliebte Kategorien
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-2.5 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-xs text-foreground-700"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className={cat.icon}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}